if (!customElements.get('royal-floating-video')) {
  customElements.define('royal-floating-video', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.preview = this.querySelector('.royal-video__preview');
      this.player = this.querySelector('.royal-video__player');
      this.dialog = this.querySelector('dialog');
      this.stage = this.querySelector('.royal-video__stage');
      this.launch = this.querySelector('.royal-video__launch');
      this.bubble = this.querySelector('.royal-video__bubble');
      this.error = this.querySelector('.royal-video__error');
      this.toggle = this.querySelector('.royal-video__toggle');
      this.mute = this.querySelector('.royal-video__mute');
      this.seek = this.querySelector('.royal-video__seek');
      this.time = this.querySelector('.royal-video__time');
      this.options = this.querySelector('.royal-video__options');
      this.menu = this.querySelector('.royal-video__menu');
      this.notice = this.querySelector('.royal-video__notice');
      this.player.controls = false;
      this.player.muted = true;
      this.hidden = false;
      this.launch.addEventListener('click', () => this.open(), { signal });
      this.querySelector('.royal-video__close').addEventListener('click', () => this.dialog.close(), { signal });
      this.querySelector('.royal-video__dismiss').addEventListener('click', () => {
        this.stopPreview();
        this.hidden = true;
        this.bubble.hidden = true;
      }, { signal });
      this.dialog.addEventListener('close', () => {
        if (!this.dialog.open) this.close();
      }, { signal });
      this.mobile = window.matchMedia('(max-width: 749px)');
      this.mobile.addEventListener('change', () => {
        if (!this.dialog.open) return;
        const focused = document.activeElement;
        // Changing between modal and floating presentation requires reopening the dialog.
        this.dialog.close();
        this.present();
        if (this.dialog.contains(focused) && focused.getClientRects().length) focused.focus({ preventScroll: true });
      }, { signal });
      window.addEventListener('keydown', event => {
        if (event.key !== 'Escape' || !this.dialog.open || !this.dialog.contains(document.activeElement)) return;
        event.preventDefault();
        event.stopPropagation();
        if (!this.menu.hidden) this.setMenu(false);
        else this.dialog.close();
      }, { signal, capture: true });
      this.toggle.addEventListener('click', () => {
        if (this.player.paused) this.play();
        else this.player.pause();
      }, { signal });
      this.mute.addEventListener('click', () => { this.player.muted = !this.player.muted; }, { signal });
      this.options.addEventListener('click', () => this.setMenu(this.menu.hidden), { signal });
      this.dialog.addEventListener('click', event => {
        if (!this.menu.contains(event.target) && !this.options.contains(event.target)) this.setMenu(false);
      }, { signal });
      this.querySelector('.royal-video__restart').addEventListener('click', () => {
        this.player.currentTime = 0;
        this.setMenu(false);
        this.play();
        this.options.focus();
      }, { signal });
      const fullscreen = this.querySelector('.royal-video__fullscreen');
      fullscreen.addEventListener('click', () => {
        this.setMenu(false);
        const expanded = this.dialog.toggleAttribute('data-expanded');
        fullscreen.textContent = expanded ? fullscreen.dataset.reduceLabel : fullscreen.dataset.expandLabel;
        this.options.focus();
      }, { signal });
      this.querySelector('.royal-video__share').addEventListener('click', event => this.copyLink(event.currentTarget), { signal });
      this.seek.addEventListener('input', () => {
        if (Number.isFinite(this.player.duration)) this.player.currentTime = Number(this.seek.value) / 100 * this.player.duration;
        this.updateProgress();
      }, { signal });
      this.player.addEventListener('error', () => { this.error.hidden = false; }, { signal });
      this.player.addEventListener('playing', () => { this.error.hidden = true; }, { signal });
      ['play', 'pause', 'volumechange', 'ended'].forEach(name => this.player.addEventListener(name, () => this.updateControls(), { signal }));
      ['timeupdate', 'durationchange', 'loadedmetadata'].forEach(name => this.player.addEventListener(name, () => this.updateProgress(), { signal }));
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          this.stopPreview();
          this.player.pause();
        }
      }, { signal });
      window.addEventListener('pagehide', () => {
        this.stopPreview();
        this.player.pause();
      }, { signal });
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.motion.addEventListener('change', () => { if (this.motion.matches) this.stopPreview(); }, { signal });
      this.schedulePosition = () => {
        cancelAnimationFrame(this.positionFrame);
        this.positionFrame = requestAnimationFrame(() => this.updatePosition());
      };
      window.addEventListener('resize', this.schedulePosition, { signal, passive: true });
      window.addEventListener('scroll', this.schedulePosition, { signal, passive: true });
      this.stickyBars = [...document.querySelectorAll('#md-sticky-atc, .hdt-sticky-btn-atc')];
      this.stickyObserver = new MutationObserver(this.schedulePosition);
      this.sizeObserver = new ResizeObserver(this.schedulePosition);
      this.stickyBars.forEach(bar => {
        this.stickyObserver.observe(bar, { attributes: true, attributeFilter: ['class', 'style', 'aria-hidden'] });
        this.sizeObserver.observe(bar);
        bar.addEventListener('transitionend', this.schedulePosition, { signal });
      });
      this.stickyObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
      this.updatePosition();
      this.updateControls();
      this.bubble.classList.add('royal-video', 'royal-video--anchored');
      this.mountBubble();
      const productSection = document.querySelector('.product-information')?.parentElement;
      if (productSection) {
        this.anchorObserver = new MutationObserver(() => this.mountBubble());
        this.anchorObserver.observe(productSection, { childList: true, subtree: true });
      }
      const start = () => { this.previewDelay = setTimeout(() => this.playPreview(), 1500); };
      if (document.readyState === 'complete') start();
      else window.addEventListener('load', start, { once: true, signal });
    }

    mountBubble() {
      const product = document.querySelector('.product-information');
      const payments = product?.querySelector('.royal-product-trust-strip__payments');
      const buyButtons = product?.querySelector('.buy-buttons-block');
      if (payments && this.bubble.parentElement !== payments) payments.prepend(this.bubble);
      else if (!payments && buyButtons && buyButtons.nextElementSibling !== this.bubble) buyButtons.after(this.bubble);
      this.bubble.hidden = this.hidden || !(payments || buyButtons);
    }

    setMenu(open) {
      const focusedInside = this.menu.contains(document.activeElement);
      this.menu.hidden = !open;
      this.options.setAttribute('aria-expanded', String(open));
      if (!open && focusedInside) this.options.focus();
    }

    updateControls() {
      this.dialog.toggleAttribute('data-playing', !this.player.paused);
      this.dialog.toggleAttribute('data-muted', this.player.muted);
      this.toggle.setAttribute('aria-label', this.player.paused ? this.toggle.dataset.playLabel : this.toggle.dataset.pauseLabel);
      this.mute.setAttribute('aria-label', this.player.muted ? this.mute.dataset.unmuteLabel : this.mute.dataset.muteLabel);
    }

    updateProgress() {
      const duration = this.player.duration;
      const ready = Number.isFinite(duration) && duration > 0;
      const seconds = Math.floor(this.player.currentTime || 0);
      const format = value => `${Math.floor(value / 60)}:${String(value % 60).padStart(2, '0')}`;
      this.seek.disabled = !ready;
      this.seek.value = ready ? this.player.currentTime / duration * 100 : 0;
      this.seek.style.setProperty('--royal-video-progress', `${this.seek.value}%`);
      this.seek.setAttribute('aria-valuetext', ready ? `${format(seconds)} / ${format(Math.floor(duration))}` : '0:00');
      this.time.textContent = ready ? `${format(seconds)} / ${format(Math.floor(duration))}` : '0:00';
      this.dialog.toggleAttribute('data-landscape', this.player.videoWidth > this.player.videoHeight);
    }

    async copyLink(button) {
      try {
        await navigator.clipboard.writeText(button.dataset.url);
        this.notice.textContent = button.dataset.copiedLabel;
      } catch {
        this.notice.textContent = button.dataset.failedLabel;
      }
      if (!this.isConnected) return;
      this.notice.hidden = false;
      clearTimeout(this.noticeDelay);
      this.noticeDelay = setTimeout(() => { this.notice.hidden = true; }, 3000);
    }

    updatePosition() {
      let lift = 0;
      this.stickyBars.forEach(bar => {
        const rect = bar.getBoundingClientRect();
        const style = getComputedStyle(bar);
        if (style.position === 'fixed' && style.visibility !== 'hidden' && Number(style.opacity) > 0 && rect.height > 0 && rect.top < window.innerHeight && rect.bottom > 0) lift = Math.max(lift, window.innerHeight - rect.top + 16);
      });
      this.style.setProperty('--royal-video-lift', `${lift}px`);
    }

    playPreview() {
      if (!this.isConnected || this.hidden || this.dialog.open || document.hidden || this.motion.matches || navigator.connection?.saveData) return;
      this.preview.muted = true;
      this.preview.src = this.preview.dataset.src;
      this.preview.play().then(() => {
        if (!this.isConnected || this.hidden || this.dialog.open || document.hidden || this.motion.matches) {
          this.preview.pause();
          return;
        }
        this.previewStop = setTimeout(() => this.preview.pause(), 5000);
      }).catch(() => {});
    }

    stopPreview() {
      clearTimeout(this.previewDelay);
      clearTimeout(this.previewStop);
      this.preview.pause();
    }

    play() {
      if (!this.player.getAttribute('src') || this.player.error) this.player.src = this.player.dataset.src;
      this.player.play().catch(() => this.updateControls());
    }

    present() {
      this.dialog.setAttribute('aria-modal', String(this.mobile.matches));
      if (this.mobile.matches) this.dialog.showModal();
      else this.dialog.show();
      document.documentElement.classList.toggle('royal-video-mobile-open', this.mobile.matches);
    }

    open() {
      this.stopPreview();
      this.error.hidden = true;
      this.present();
      this.setAttribute('data-open', '');
      this.bubble.setAttribute('data-open', '');
      this.launch.setAttribute('aria-expanded', 'true');
      this.play();
    }

    close() {
      document.documentElement.classList.remove('royal-video-mobile-open');
      this.player.pause();
      this.setMenu(false);
      this.removeAttribute('data-open');
      this.bubble.removeAttribute('data-open');
      this.launch.setAttribute('aria-expanded', 'false');
      this.notice.hidden = true;
      this.dialog.removeAttribute('data-expanded');
      const fullscreen = this.querySelector('.royal-video__fullscreen');
      fullscreen.textContent = fullscreen.dataset.expandLabel;
      if (this.isConnected && !this.hidden) this.launch.focus({ preventScroll: true });
    }

    disconnectedCallback() {
      this.anchorObserver?.disconnect();
      this.append(this.bubble);
      this.stopPreview();
      clearTimeout(this.noticeDelay);
      this.player.pause();
      if (this.dialog.open) {
        this.dialog.close();
        document.documentElement.classList.remove('royal-video-mobile-open');
      }
      this.controller.abort();
      this.controller = null;
      this.stickyObserver.disconnect();
      this.sizeObserver.disconnect();
      cancelAnimationFrame(this.positionFrame);
    }
  });
}
